import { DockviewApi, DockviewPanelApi } from "dockview-react";
import React, { useCallback, useEffect, useRef } from "react";
import { api } from "./utils/api";
import { unixTimestampToNewYork } from "./utils/utils";
import { useReadAloud, ReadableItem } from "./utils/useReadAloud";
import { ReadAloudBar } from "./utils/ReadAloudBar";
import { ArticleViewer } from "./ArticleViewer";
import { useCtrlDMark } from "./utils/useCtrlDMark";
import { HighlightedText, Mark } from "./utils/HighlightedText";

interface IReaction {
  id: number;
  article_url: string;
  asset: string;
  asset_marks: Mark[];
  event: string;
  event_marks: Mark[];
  reaction: string;
  reaction_marks: Mark[];
  future_event: boolean;
  earnings_event: boolean;
  is_company: boolean;
  mark: number;
  published_at: number;
  title: string;
  title_marks: Mark[];
  source: string;
  tickers: string;
}

interface IReactionSummary {
  id: number;
  asset: string;
  asset_marks: Mark[];
  event: string;
  event_marks: Mark[];
  reaction: string;
  reaction_marks: Mark[];
  future_event: boolean;
  earnings_event: boolean;
  is_company: boolean;
  mark: number;
  generation_timestamp: string | null;
  parent_ids: number[];
}

interface IParentReaction {
  id: number;
  asset: string;
  event: string;
  reaction: string;
  future_event: boolean;
  earnings_event: boolean;
  is_company: boolean;
  published_at: number;
  article_url: string;
  title: string;
  source: string;
  tickers: string;
}

interface IReactionsRequest {
  start_date: string;
  end_date: string;
  query: string;
  future_event: boolean | null;
  earnings_event: boolean | null;
  is_company: boolean | null;
  mark_filter: number | null;
  page: number;
}

type ActiveTab = "feed" | "summary";

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function lastMonthStr() {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function addMarkClass(mark: number): string {
  switch (mark) {
    case 0: return "bg-gray-400";
    case 1: return "bg-blue-600";
    case 2: return "bg-yellow-600";
    default: return "bg-gray-400";
  }
}

function minutesAgo(isoStr: string | null): string {
  if (!isoStr) return "";
  const normalized = isoStr.includes("+") || isoStr.toUpperCase().includes("Z") ? isoStr : isoStr + "Z";
  const mins = Math.floor((Date.now() - new Date(normalized).getTime()) / 60000);
  return `${mins} min ago`;
}

function formatTimestamp(ts: string | null): string {
  if (!ts) return "";
  const normalized = ts.includes("+") || ts.toUpperCase().includes("Z") ? ts : ts + "Z";
  const date = new Date(normalized);
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    weekday: "short",
    hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(date).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute} (${parts.weekday})`;
}


export const Reactions = (props: { api?: DockviewPanelApi; containerApi?: DockviewApi; isMobile?: boolean }) => {
  const [activeTab, setActiveTab] = React.useState<ActiveTab>("feed");

  // Feed tab state
  const [reactions, setReactions] = React.useState<IReaction[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isFeedLoadingMore, setIsFeedLoadingMore] = React.useState(false);
  const [feedHasMore, setFeedHasMore] = React.useState(false);
  const [error, setError] = React.useState("");
  const [activeArticleUrl, setActiveArticleUrl] = React.useState("");
  const [request, setRequest] = React.useState<IReactionsRequest>({
    start_date: lastMonthStr(),
    end_date: todayStr(),
    query: "",
    future_event: null,
    earnings_event: null,
    is_company: true,
    mark_filter: 0,
    page: 1,
  });

  // Summary tab state
  const [summaryReactions, setSummaryReactions] = React.useState<IReactionSummary[]>([]);
  const [summaryCount, setSummaryCount] = React.useState<number | null>(null);
  const [inProgress, setInProgress] = React.useState(false);
  const [startedAt, setStartedAt] = React.useState<string | null>(null);
  const [isSummaryLoading, setIsSummaryLoading] = React.useState(false);
  const [isSummaryLoadingMore, setIsSummaryLoadingMore] = React.useState(false);
  const [summaryHasMore, setSummaryHasMore] = React.useState(false);
  const [summaryError, setSummaryError] = React.useState("");
  const [genError, setGenError] = React.useState<{ message: string; trace: string } | null>(null);
  const [onlyLast, setOnlyLast] = React.useState(true);
  const [expandedParents, setExpandedParents] = React.useState<Record<number, IParentReaction[] | "loading">>({});
  const [summaryRequest, setSummaryRequest] = React.useState<IReactionsRequest>({
    start_date: lastMonthStr(),
    end_date: todayStr(),
    query: "",
    future_event: null,
    earnings_event: null,
    is_company: true,
    mark_filter: 0,
    page: 1,
  });

  const feedScrollRef = useRef<HTMLDivElement>(null);
  const summaryScrollRef = useRef<HTMLDivElement>(null);

  const feedReadAloud = useReadAloud([reactions]);
  const summaryReadAloud = useReadAloud([summaryReactions]);

  useEffect(() => {
    fetchReactions(request);
  }, []);

  useEffect(() => {
    if (activeTab === "summary") {
      fetchSummaryReactions(summaryRequest);
      fetchSummaryCount();
    }
  }, [activeTab]);

  function fetchReactions(req: IReactionsRequest, append = false) {
    if (append) setIsFeedLoadingMore(true); else setIsLoading(true);
    setError("");
    api
      .post<IReaction[]>("/reactions", req)
      .then((res) => {
        setReactions((prev) => (append ? [...prev, ...res.data] : res.data));
        setFeedHasMore(res.data.length === 100);
      })
      .catch(() => setError("Failed to fetch reactions."))
      .finally(() => { if (append) setIsFeedLoadingMore(false); else setIsLoading(false); });
  }

  function fetchSummaryReactions(req: IReactionsRequest, append = false) {
    if (append) setIsSummaryLoadingMore(true); else setIsSummaryLoading(true);
    setSummaryError("");
    api
      .post<IReactionSummary[]>("/reactions-summary/search", req)
      .then((res) => {
        setSummaryReactions((prev) => (append ? [...prev, ...res.data] : res.data));
        setSummaryHasMore(res.data.length === 100);
      })
      .catch(() => setSummaryError("Failed to fetch summary reactions."))
      .finally(() => { if (append) setIsSummaryLoadingMore(false); else setIsSummaryLoading(false); });
  }

  const fetchSummaryCount = async () => {
    try {
      const resp = await api.get<{
        count: number;
        in_progress: boolean;
        started_at: string | null;
        error: boolean;
        error_message: string | null;
        error_trace: string | null;
      }>("/reactions-summary/count");
      setSummaryCount(resp.data.count);
      setInProgress(resp.data.in_progress);
      setStartedAt(resp.data.started_at);
      setGenError(
        resp.data.error && resp.data.error_message
          ? { message: resp.data.error_message, trace: resp.data.error_trace ?? "" }
          : null
      );
    } catch {
      console.error("Error fetching reactions summary count");
    }
  };

  const generateSummaries = async () => {
    setSummaryError("");
    setGenError(null);
    try {
      const resp = await api.post<{ started: boolean; in_progress: boolean; started_at: string | null }>("/reactions-summary/generate");
      setInProgress(resp.data.in_progress);
      setStartedAt(resp.data.started_at);
    } catch {
      setSummaryError("Error while generating summaries");
    }
  };

  const toggleParents = async (itemId: number, parentIds: number[]) => {
    if (expandedParents[itemId]) {
      setExpandedParents((prev) => { const next = { ...prev }; delete next[itemId]; return next; });
      return;
    }
    if (!parentIds.length) return;
    setExpandedParents((prev) => ({ ...prev, [itemId]: "loading" }));
    try {
      const resp = await api.get<{ results: IParentReaction[] }>("/reactions-by-ids", {
        params: { ids: parentIds.join(",") },
      });
      setExpandedParents((prev) => ({ ...prev, [itemId]: resp.data.results }));
    } catch {
      setExpandedParents((prev) => { const next = { ...prev }; delete next[itemId]; return next; });
    }
  };

  const markReaction = useCallback((id: number, newMark: number) => {
    const cycled = newMark % 3;
    const prev = reactions.find((r) => r.id === id)?.mark ?? 0;
    setReactions((rs) => rs.map((r) => (r.id === id ? { ...r, mark: cycled } : r)));
    api
      .post("/reactions/mark", { id, mark: cycled })
      .catch(() => setReactions((rs) => rs.map((r) => (r.id === id ? { ...r, mark: prev } : r))));
  }, [reactions]);

  const markSummaryReaction = useCallback((id: number, newMark: number) => {
    const cycled = newMark % 3;
    const summaryItem = summaryReactions.find((r) => r.id === id);
    const prev = summaryItem?.mark ?? 0;
    setSummaryReactions((rs) => rs.map((r) => (r.id === id ? { ...r, mark: cycled } : r)));
    api
      .post("/reactions-summary/mark", null, { params: { id, mark: cycled } })
      .catch(() => setSummaryReactions((rs) => rs.map((r) => (r.id === id ? { ...r, mark: prev } : r))));
    if (cycled > 0 && summaryItem?.parent_ids?.length) {
      const parentIds = summaryItem.parent_ids;
      setReactions((rs) => rs.map((r) => parentIds.includes(r.id) ? { ...r, mark: 1 } : r));
      api
        .post("/reactions/mark-batch", { ids: parentIds, mark: 1 })
        .catch(() => setReactions((rs) => rs.map((r) => parentIds.includes(r.id) ? { ...r, mark: 0 } : r)));
    }
  }, [summaryReactions]);

  const feedReadableItems: ReadableItem[] = reactions
    .filter((r) => r.mark === 0)
    .map((r) => ({
      key: String(r.id),
      texts: [`${r.asset}. ${r.event}. ${r.reaction}`],
      onTextDone: () => markReaction(r.id, 1),
    }));

  const summaryReadableItems: ReadableItem[] = summaryReactions
    .filter((r) => r.mark === 0)
    .map((r) => ({
      key: `summary-${r.id}`,
      texts: [`${r.asset}. ${r.event}. ${r.reaction}`],
      onTextDone: () => markSummaryReaction(r.id, 1),
    }));

  const handleCtlD = useCallback(() => {
    if (activeTab === "summary") {
      const firstUnmarked = summaryReactions.find((r) => r.mark === 0);
      if (firstUnmarked) markSummaryReaction(firstUnmarked.id, 1);
    } else {
      const firstUnmarked = reactions.find((r) => r.mark === 0);
      if (firstUnmarked) markReaction(firstUnmarked.id, 1);
    }
  }, [activeTab, reactions, summaryReactions, markReaction, markSummaryReaction]);

  const getNextScrollKey = useCallback(() => {
    if (activeTab === "summary") {
      const unmarked = summaryReactions.filter((r) => r.mark === 0);
      return unmarked[1] ? `summary-${unmarked[1].id}` : null;
    }
    const unmarked = reactions.filter((r) => r.mark === 0);
    return unmarked[1] ? String(unmarked[1].id) : null;
  }, [activeTab, reactions, summaryReactions]);

  useCtrlDMark({ onTrigger: handleCtlD, getNextScrollKey, panelApi: props.api, containerApi: props.containerApi });

  const onFeedItemDone = useCallback((_key: string) => {}, []);
  const onSummaryItemDone = useCallback((_key: string) => {}, []);

  function daysAgo(publishedAt: number): string {
    const days = Math.floor((Date.now() - publishedAt * 1000) / (1000 * 60 * 60 * 24));
    return `${days}d`;
  }

  function handleFeedSubmit(e: React.FormEvent) {
    e.preventDefault();
    const next = { ...request, page: 1 };
    setRequest(next);
    fetchReactions(next);
  }

  function loadMoreFeedReactions() {
    const next = { ...request, page: request.page + 1 };
    setRequest(next);
    fetchReactions(next, true);
  }

  function handleSummarySubmit(e: React.FormEvent) {
    e.preventDefault();
    const next = { ...summaryRequest, page: 1 };
    setSummaryRequest(next);
    fetchSummaryReactions(next);
  }

  function loadMoreSummaryReactions() {
    const next = { ...summaryRequest, page: summaryRequest.page + 1 };
    setSummaryRequest(next);
    fetchSummaryReactions(next, true);
  }

  function renderLoadMore(hasMore: boolean, isLoadingMore: boolean, onLoadMore: () => void) {
    if (!hasMore) return null;
    return (
      <div className="flex justify-center my-4">
        <button
          type="button"
          className="btn btn-default disabled:opacity-60 disabled:cursor-default"
          disabled={isLoadingMore}
          onClick={onLoadMore}
        >
          {isLoadingMore ? "Loading…" : "Load more"}
        </button>
      </div>
    );
  }

  const activeReadAloud = activeTab === "summary" ? summaryReadAloud : feedReadAloud;
  const activeReadableItems = activeTab === "summary" ? summaryReadableItems : feedReadableItems;
  const activeOnItemDone = activeTab === "summary" ? onSummaryItemDone : onFeedItemDone;
  const activeIdPrefix = activeTab === "summary" ? "summary-" : "";

  const summaryFiltered = React.useMemo(() => {
    if (!onlyLast || summaryReactions.length === 0) return summaryReactions;
    const lastTs = summaryReactions.reduce<string | null>((best, r) => {
      if (!r.generation_timestamp) return best;
      if (!best || r.generation_timestamp > best) return r.generation_timestamp;
      return best;
    }, null);
    return lastTs ? summaryReactions.filter((r) => r.generation_timestamp === lastTs) : summaryReactions;
  }, [summaryReactions, onlyLast]);

  function renderFiltersForm(
    req: IReactionsRequest,
    setReq: React.Dispatch<React.SetStateAction<IReactionsRequest>>,
    onSubmit: (e: React.FormEvent) => void,
    labelPrefix: string
  ) {
    return (
      <form className="flex flex-col mt-4 mb-4 gap-2" onSubmit={onSubmit}>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="form-group">
            <label htmlFor={`${labelPrefix}-start`}>Start:</label>
            <input
              id={`${labelPrefix}-start`}
              type="text"
              value={req.start_date}
              onChange={(e) => setReq((r) => ({ ...r, start_date: e.target.value }))}
              placeholder="YYYY-MM-DD"
            />
          </div>
          <div className="form-group">
            <label htmlFor={`${labelPrefix}-end`}>End:</label>
            <input
              id={`${labelPrefix}-end`}
              type="text"
              value={req.end_date}
              onChange={(e) => setReq((r) => ({ ...r, end_date: e.target.value }))}
              placeholder="YYYY-MM-DD"
            />
          </div>
          <div className="form-group w-64">
            <label htmlFor={`${labelPrefix}-query`}>Search:</label>
            <input
              id={`${labelPrefix}-query`}
              type="text"
              value={req.query}
              onChange={(e) => setReq((r) => ({ ...r, query: e.target.value }))}
              placeholder="FTS query…"
            />
          </div>
          <div className="flex flex-wrap gap-3 items-start">
            <div className="form-group w-32">
              <label htmlFor={`${labelPrefix}-future`}>Future event:</label>
              <select
                id={`${labelPrefix}-future`}
                value={req.future_event === null ? "" : String(req.future_event)}
                onChange={(e) => setReq((r) => ({ ...r, future_event: e.target.value === "" ? null : e.target.value === "true" }))}
                className="border border-gray-300 rounded p-1 block w-full"
              >
                <option value="">Any</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div className="form-group w-36">
              <label htmlFor={`${labelPrefix}-earnings`}>Earnings event:</label>
              <select
                id={`${labelPrefix}-earnings`}
                value={req.earnings_event === null ? "" : String(req.earnings_event)}
                onChange={(e) => setReq((r) => ({ ...r, earnings_event: e.target.value === "" ? null : e.target.value === "true" }))}
                className="border border-gray-300 rounded p-1 block w-full"
              >
                <option value="">Any</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div className="form-group w-32">
              <label htmlFor={`${labelPrefix}-company`}>Company:</label>
              <select
                id={`${labelPrefix}-company`}
                value={req.is_company === null ? "" : String(req.is_company)}
                onChange={(e) => setReq((r) => ({ ...r, is_company: e.target.value === "" ? null : e.target.value === "true" }))}
                className="border border-gray-300 rounded p-1 block w-full"
              >
                <option value="">Any</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div className="form-group w-24">
              <label htmlFor={`${labelPrefix}-mark`}>Mark:</label>
              <select
                id={`${labelPrefix}-mark`}
                value={req.mark_filter === null ? "" : String(req.mark_filter)}
                onChange={(e) => setReq((r) => ({ ...r, mark_filter: e.target.value === "" ? null : Number(e.target.value) }))}
                className="border border-gray-300 rounded p-1 block w-full"
              >
                <option value="">Any</option>
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2</option>
              </select>
            </div>
            <button type="submit" className="btn btn-default">
              Search
            </button>
          </div>
        </div>
      </form>
    );
  }

  return (
    <div className={`relative flex flex-col h-full ${props.isMobile ? "w-full" : ""}`}>
      {/* Tab header */}
      <div className="flex-shrink-0 flex items-center gap-4 px-2 py-1.5 border-b border-gray-200">
        {props.isMobile && (
          <a href="/mobile" className="btn btn-default">To Menu</a>
        )}
        <label className="flex items-center gap-1.5 cursor-pointer select-none text-sm">
          <input
            type="radio"
            name="reactions-tab"
            value="feed"
            checked={activeTab === "feed"}
            onChange={() => setActiveTab("feed")}
          />
          Feed
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer select-none text-sm">
          <input
            type="radio"
            name="reactions-tab"
            value="summary"
            checked={activeTab === "summary"}
            onChange={() => setActiveTab("summary")}
          />
          Summary
        </label>
      </div>

      {/* Feed tab */}
      <div
        ref={feedScrollRef}
        className={`flex-1 overflow-auto px-4 ${activeTab !== "feed" ? "hidden" : ""}`}
      >
        {renderFiltersForm(request, setRequest, handleFeedSubmit, "r")}

        {isLoading && <div>Loading…</div>}
        {error && <p className="text-red-500">{error}</p>}
        {!isLoading && !error && reactions.length === 0 && <p>No reactions found.</p>}

        <div className="mb-20">
          {reactions.map((r) => {
            const isReading = feedReadAloud.currentlyReadingKey === String(r.id);
            return (
              <div
                key={r.id}
                id={String(r.id)}
                className={`flex gap-4 mb-6 ${isReading ? "bg-yellow-100" : ""}`}
              >
                <div
                  className={`min-w-3 cursor-pointer ${addMarkClass(r.mark)}`}
                  onClick={() => markReaction(r.id, r.mark + 1)}
                />
                <div className="flex-1">
                  <p className="font-bold"><HighlightedText text={r.asset} marks={r.asset_marks} /></p>
                  <p className="text-gray-800"><HighlightedText text={r.event} marks={r.event_marks} /></p>
                  <p className="text-gray-600 mb-3"><HighlightedText text={r.reaction} marks={r.reaction_marks} /></p>
                  {r.future_event && <span className="text-xs bg-blue-200 px-1 mr-1">future</span>}
                  {r.earnings_event && <span className="text-xs bg-green-200 px-1 mr-1">earnings</span>}
                  {r.is_company && <span className="text-xs bg-purple-200 px-1 mr-1">company</span>}
                  <p className="mt-2">
                    {unixTimestampToNewYork(r.published_at)[0]} {`(${daysAgo(r.published_at)} ago)`}
                  </p>
                  {r.tickers && <mark className="bg-orange-400">{r.tickers}</mark>}
                  <br />
                  <div className="whitespace-pre-wrap inline">
                    <HighlightedText text={r.title} marks={r.title_marks} />
                  </div>
                  <br />
                  <p className="text-gray-500 gap-4 flex">
                    <a href={r.article_url} target="_blank" rel="noopener noreferrer">
                      ({r.source})
                    </a>
                    <a
                      href="/"
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveArticleUrl(r.article_url);
                      }}
                    >
                      (text)
                    </a>
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {renderLoadMore(feedHasMore, isFeedLoadingMore, loadMoreFeedReactions)}
      </div>

      {/* Summary tab */}
      <div
        ref={summaryScrollRef}
        className={`flex-1 overflow-auto px-4 ${activeTab !== "summary" ? "hidden" : ""}`}
      >
        <div className="flex items-center gap-3 mt-4 mb-2 flex-wrap">
          {inProgress ? (
            <span className="text-sm text-blue-600">
              Generation in progress — started {minutesAgo(startedAt)}
            </span>
          ) : summaryCount !== null ? (
            <span className="text-sm text-gray-600">
              {summaryCount} feed item{summaryCount !== 1 ? "s" : ""} ready (last 3 days, unread)
            </span>
          ) : null}
          <button
            type="button"
            className="btn btn-default disabled:opacity-60 disabled:cursor-default"
            disabled={inProgress}
            onClick={generateSummaries}
          >
            Generate summaries
          </button>
        </div>

        {genError && (
          <div className="mb-4 text-red-600 text-sm border border-red-300 rounded p-2 bg-red-50">
            <p className="font-semibold mb-1">{genError.message}</p>
            <pre className="text-xs overflow-auto max-h-40 whitespace-pre-wrap break-all">{genError.trace}</pre>
          </div>
        )}

        {renderFiltersForm(summaryRequest, setSummaryRequest, handleSummarySubmit, "sr")}

        {isSummaryLoading && <div>Loading…</div>}
        {summaryError && <p className="text-red-500">{summaryError}</p>}
        {!isSummaryLoading && !summaryError && summaryReactions.length === 0 && (
          <p>No summary reactions found.</p>
        )}

        {summaryReactions.length > 0 && (
          <>
            <label className="flex items-center gap-1.5 text-sm text-gray-500 mb-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={onlyLast}
                onChange={(e) => setOnlyLast(e.target.checked)}
                className="cursor-pointer"
              />
              Only last
            </label>

            <div className="mb-20">
              {summaryFiltered.map((r) => {
                const isReading = summaryReadAloud.currentlyReadingKey === `summary-${r.id}`;
                const parentIds = r.parent_ids ?? [];
                const parentState = expandedParents[r.id];
                const isExpanded = !!parentState;
                return (
                  <div
                    key={r.id}
                    id={`summary-${r.id}`}
                    className={`flex gap-4 mb-6 ${isReading ? "bg-yellow-100" : ""}`}
                  >
                    <div
                      className={`min-w-3 cursor-pointer ${addMarkClass(r.mark)}`}
                      onClick={() => markSummaryReaction(r.id, r.mark + 1)}
                    />
                    <div className="flex-1">
                      <p className="text-gray-500 text-sm mb-1 flex flex-wrap gap-2">
                        <span>{formatTimestamp(r.generation_timestamp)}</span>
                        {parentIds.length > 0 && (
                          <span className="text-xs text-gray-400">
                            {parentIds.length} source{parentIds.length !== 1 ? "s" : ""}
                          </span>
                        )}
                      </p>
                      <p className="font-bold"><HighlightedText text={r.asset} marks={r.asset_marks} /></p>
                      <p className="text-gray-800"><HighlightedText text={r.event} marks={r.event_marks} /></p>
                      <p className="text-gray-600 mb-3 whitespace-pre-wrap">
                        <HighlightedText text={r.reaction} marks={r.reaction_marks} />
                      </p>
                      {r.future_event && <span className="text-xs bg-blue-200 px-1 mr-1">future</span>}
                      {r.earnings_event && <span className="text-xs bg-green-200 px-1 mr-1">earnings</span>}
                      {r.is_company && <span className="text-xs bg-purple-200 px-1 mr-1">company</span>}

                      {parentIds.length > 0 && (
                        <>
                          <button
                            type="button"
                            className="mt-2 text-xs text-gray-400 hover:text-gray-700 block"
                            onClick={() => toggleParents(r.id, parentIds)}
                          >
                            {isExpanded ? "▲ Hide sources" : `▼ Show ${parentIds.length} source${parentIds.length !== 1 ? "s" : ""}`}
                          </button>
                          {isExpanded && (
                            <div className="mt-2 border-l-2 border-gray-300 pl-3 space-y-4">
                              {parentState === "loading" ? (
                                <p className="text-xs text-gray-400">Loading…</p>
                              ) : (parentState as IParentReaction[]).map((p) => (
                                <div key={p.id} className="text-sm">
                                  <p className="font-bold">{p.asset}</p>
                                  <p className="text-gray-800">{p.event}</p>
                                  <p className="text-gray-600 mb-2 whitespace-pre-wrap">{p.reaction}</p>
                                  <div className="mb-1">
                                    {p.future_event && <span className="text-xs bg-blue-200 px-1 mr-1">future</span>}
                                    {p.earnings_event && <span className="text-xs bg-green-200 px-1 mr-1">earnings</span>}
                                    {p.is_company && <span className="text-xs bg-purple-200 px-1 mr-1">company</span>}
                                  </div>
                                  <p>{unixTimestampToNewYork(p.published_at)[0]} ({daysAgo(p.published_at)} ago)</p>
                                  {p.tickers && <mark className="bg-orange-400">{p.tickers}</mark>}
                                  {p.tickers && <br />}
                                  <span className="whitespace-pre-wrap">{p.title}</span>
                                  <p className="text-gray-500 flex gap-4 mt-0.5">
                                    <a href={p.article_url} target="_blank" rel="noopener noreferrer">({p.source})</a>
                                    <a href="/" onClick={(e) => { e.preventDefault(); setActiveArticleUrl(p.article_url); }}>(text)</a>
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {renderLoadMore(summaryHasMore, isSummaryLoadingMore, loadMoreSummaryReactions)}
          </>
        )}
      </div>

      <ReadAloudBar
        voiceControls={activeReadAloud.voiceControls}
        setVoiceControls={activeReadAloud.setVoiceControls}
        toggleReading={activeReadAloud.toggleReading}
        skipItem={activeReadAloud.skipItem}
        items={activeReadableItems}
        onItemDone={activeOnItemDone}
        idPrefix={activeIdPrefix}
        containerApi={props.containerApi!}
        panelApi={props.api!}
      />

      {activeArticleUrl !== "" && (
        <ArticleViewer url={activeArticleUrl} setActiveArticleUrl={setActiveArticleUrl} />
      )}
    </div>
  );
};
